import { PostHog } from '../posthog-core'
import { DEAD_CLICKS_ENABLED_SERVER_SIDE } from '../constants'
import { isBoolean, isObject } from '@posthog/core'
import { document } from '@posthog/browser-common/utils/globals'
import { assignableWindow, LazyLoadedDeadClicksAutocaptureInterface } from '../utils/globals'
import { createLogger } from '@posthog/browser-common/utils/logger'
import { DeadClicksAutoCaptureConfig, RemoteConfigResult } from '../types'
import type { Extension } from './types'

const logger = createLogger('[Dead Clicks]')

export const isDeadClicksEnabledForHeatmaps = () => {
    return true
}
export const isDeadClicksEnabledForAutocapture = (instance: DeadClicksAutocapture) => {
    const isRemoteEnabled = !!instance.instance.persistence?.get_property(DEAD_CLICKS_ENABLED_SERVER_SIDE)
    const clientConfig = instance.instance.config.capture_dead_clicks
    if (isBoolean(clientConfig)) {
        return clientConfig
    }
    if (isObject(clientConfig)) {
        return true
    }
    return isRemoteEnabled
}

export class DeadClicksAutocapture implements Extension {
    get lazyLoadedDeadClicksAutocapture(): LazyLoadedDeadClicksAutocaptureInterface | undefined {
        return this._lazyLoadedDeadClicksAutocapture
    }

    private _lazyLoadedDeadClicksAutocapture: LazyLoadedDeadClicksAutocaptureInterface | undefined

    constructor(
        readonly instance: PostHog,
        readonly isEnabled: (dca: DeadClicksAutocapture) => boolean,
        readonly onCapture?: DeadClicksAutoCaptureConfig['__onCapture']
    ) {
        this.startIfEnabledOrStop()
    }

    public onRemoteConfig(result: RemoteConfigResult) {
        if (!result.ok) {
            // Failure behaves like a response without a captureDeadClicks key.
            return
        }

        const response = result.config
        if (!('captureDeadClicks' in response)) {
            return
        }

        if (this.instance.persistence) {
            this.instance.persistence.register({
                [DEAD_CLICKS_ENABLED_SERVER_SIDE]: response.captureDeadClicks,
            })
        }
        this.startIfEnabledOrStop()
    }

    public startIfEnabledOrStop() {
        if (this.isEnabled(this)) {
            this._loadScript(() => {
                this._start()
            })
        } else {
            this.stop()
        }
    }

    private _loadScript(cb: () => void): void {
        if (assignableWindow.__PosthogExtensions__?.initDeadClicksAutocapture) {
            // already loaded
            cb()
            return
        }
        assignableWindow.__PosthogExtensions__?.loadExternalDependency?.(
            this.instance,
            'dead-clicks-autocapture',
            (err) => {
                if (err) {
                    logger.error('failed to load script', err)
                    return
                }
                cb()
            }
        )
    }

    private _start() {
        if (!document) {
            logger.error('`document` not found. Cannot start.')
            return
        }

        if (
            !this._lazyLoadedDeadClicksAutocapture &&
            assignableWindow.__PosthogExtensions__?.initDeadClicksAutocapture
        ) {
            // copied so the writes below can't leak into the user's config object, which is
            // shared between the autocapture and heatmaps instances of this extension
            const config: DeadClicksAutoCaptureConfig = isObject(this.instance.config.capture_dead_clicks)
                ? { ...this.instance.config.capture_dead_clicks }
                : {}
            config.__onCapture = this.onCapture
            if (this.onCapture) {
                // an external consumer (heatmaps) only plots click points, so it never observes swipes
                config.capture_dead_swipes = false
            }

            this._lazyLoadedDeadClicksAutocapture = assignableWindow.__PosthogExtensions__.initDeadClicksAutocapture(
                this.instance,
                config
            )
            this._lazyLoadedDeadClicksAutocapture.start(document)
            logger.info(`starting...`)
        }
    }

    stop() {
        if (this._lazyLoadedDeadClicksAutocapture) {
            this._lazyLoadedDeadClicksAutocapture.stop()
            this._lazyLoadedDeadClicksAutocapture = undefined
            logger.info(`stopping...`)
        }
    }
}
